import type { CSSProperties, HTMLAttributes, ReactNode } from 'react'

interface SafeAreaInsetsProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode
  /**
   * Minimum padding on each side (default 0). Resolves to
   * `max(env(safe-area-inset-X, 0px), minX)` so callers can guarantee a
   * sensible visual gutter even when the device reports a 0 inset.
   */
  minTop?: number | string
  minBottom?: number | string
  minLeft?: number | string
  minRight?: number | string
}

function toExpr(side: 'top' | 'bottom' | 'left' | 'right', min: number | string | undefined): string {
  if (min === undefined || min === 0 || min === '0' || min === '0px') {
    return `env(safe-area-inset-${side}, 0px)`
  }
  const minStr = typeof min === 'number' ? `${min}px` : min
  return `max(env(safe-area-inset-${side}, 0px), ${minStr})`
}

/**
 * Full-bleed safe-area shell — pads top, bottom, left, AND right with the
 * device's safe-area insets. Use for full-screen overlays (incoming-call
 * takeover, fullscreen modal) where every edge must clear the notch / home
 * indicator / curved corners.
 *
 * Companion to {@link SafeAreaBottom} / {@link SafeAreaTop} / {@link MobileDockShell}.
 * Same rule: never hand-roll `env(safe-area-inset-*)` outside the primitives
 * (CLAUDE.md §8, regressions FOR-1544/1584/1597, FND-ui-1, FND-M-001/U-002).
 */
export function SafeAreaInsets({
  children,
  minTop,
  minBottom,
  minLeft,
  minRight,
  style,
  ...props
}: SafeAreaInsetsProps) {
  const composed: CSSProperties = {
    paddingTop: toExpr('top', minTop),
    paddingBottom: toExpr('bottom', minBottom),
    paddingLeft: toExpr('left', minLeft),
    paddingRight: toExpr('right', minRight),
    ...style,
  }

  return (
    <div style={composed} {...props}>
      {children}
    </div>
  )
}
